import React from "react";
import { Box } from "braid-design-system";
import Loading from "../components/Loading";
import { useToast } from "../hooks/useToast";
import { useCanManageMasters } from "../hooks/useCanManageMasters";
import { getAllItems, createItem, updateItem, ItemResponse } from "../services/itemService";
import { getAllItemGroups, ItemGroupResponse } from "../services/itemGroupService";
import CreateUpdateItemDrawer, { ItemFormData, ItemToEdit } from "../components/Item/CreateUpdateItemDrawer";
import ItemListTable from "../components/Item/ItemListTable";

const PAGE_SIZE = 100;

const ItemManagement: React.FC = () => {
    const toast = useToast();
    const canManageMasters = useCanManageMasters();
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(null);
    const [itemList, setItemList] = React.useState<ItemResponse[]>([]);
    const [itemGroupList, setItemGroupList] = React.useState<ItemGroupResponse[]>([]);
    const [itemDrawerOpen, setItemDrawerOpen] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState<ItemToEdit | undefined>(undefined);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [hasNextPage, setHasNextPage] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [selectedStatus, setSelectedStatus] = React.useState("");

    const fetchItems = React.useCallback(async (page: number = currentPage) => {
        try {
            setLoading(true);
            setLoadingMessage("Please wait, Fetching items...");
            const data = await getAllItems(page, PAGE_SIZE, {
                search: searchTerm,
                status: selectedStatus,
            });
            setItemList(data);
            setHasNextPage(data.length === PAGE_SIZE);
        } catch (error: any) {
            toast.showError(`Error fetching items: ${error?.message || "Unknown error"}`);
            setItemList([]);
            setHasNextPage(false);
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    }, [currentPage, searchTerm, selectedStatus, toast]);

    const fetchItemGroups = React.useCallback(async () => {
        try {
            const data = await getAllItemGroups();
            setItemGroupList(data);
        } catch (error: any) {
            toast.showError(`Error fetching item groups: ${error?.message || "Unknown error"}`);
            setItemGroupList([]);
        }
    }, [toast]);

    React.useEffect(() => {
        fetchItems(currentPage);
    }, [currentPage, fetchItems]);

    React.useEffect(() => {
        fetchItemGroups();
    }, [fetchItemGroups]);

    const handleSaveItem = React.useCallback(async (itemData: ItemFormData, editingItem?: ItemToEdit) => {
        if (editingItem?.item_code) {
            await updateItem(editingItem.item_code, {
                ...itemData,
                item_avgcost: editingItem.item_avgcost ?? null,
            });
        } else {
            await createItem(itemData);
        }
    }, []);

    const handleEditItem = React.useCallback((itemCode: string) => {
        const item = itemList.find(i => i.item_code === itemCode);
        if (item) {
            setSelectedItem(item as ItemToEdit);
            setItemDrawerOpen(true);
        }
    }, [itemList]);

    const handleCreateItem = React.useCallback(() => {
        setSelectedItem(undefined);
        setItemDrawerOpen(true);
    }, []);

    const handleCloseDrawer = React.useCallback(() => {
        setItemDrawerOpen(false);
        setSelectedItem(undefined);
    }, []);

    const handlePreviousPage = React.useCallback(() => {
        setCurrentPage((page) => Math.max(1, page - 1));
    }, []);

    const handleNextPage = React.useCallback(() => {
        if (hasNextPage) {
            setCurrentPage((page) => page + 1);
        }
    }, [hasNextPage]);

    const handleSearchChange = React.useCallback((value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    }, []);

    const handleStatusChange = React.useCallback((value: string) => {
        setSelectedStatus(value);
        setCurrentPage(1);
    }, []);

    return (
        <>
            {loading && <Loading message={loadingMessage || "Loading..."} />}

            <Box padding="medium">
                <ItemListTable
                    itemList={itemList}
                    onEditItem={handleEditItem}
                    onCreateItem={canManageMasters ? handleCreateItem : undefined}
                    currentPage={currentPage}
                    hasNextPage={hasNextPage}
                    onPreviousPage={handlePreviousPage}
                    onNextPage={handleNextPage}
                    searchTerm={searchTerm}
                    selectedStatus={selectedStatus}
                    onSearchChange={handleSearchChange}
                    onStatusChange={handleStatusChange}
                />
            </Box>

            <CreateUpdateItemDrawer
                isOpen={itemDrawerOpen}
                onClose={handleCloseDrawer}
                itemToEdit={selectedItem}
                itemGroups={itemGroupList}
                onSave={handleSaveItem}
                onItemSaved={() => fetchItems(currentPage)}
                isReadOnly={!canManageMasters}
            />
        </>
    );
};

export default ItemManagement;
